/**
 * Auto-Generated Selectors Utility
 * 
 * This utility wraps a Zustand store to provide auto-generated selector hooks.
 * Instead of: useBoundStore((state) => state.property)
 * You can use: useBoundStore.use.property()
 * 
 * Benefits:
 * - Type-safe with full auto-complete
 * - Less boilerplate
 * - Consistent API across the codebase
 * 
 * @see https://zustand.docs.pmnd.rs/guides/auto-generating-selectors
 */

import type { StoreApi, UseBoundStore } from 'zustand';
import type { WithSelectors } from './types';

/**
 * Wraps a Zustand store with auto-generated selectors.
 * 
 * @example
 * const useBearStoreBase = create<BearState>()(...)
 * const useBearStore = createSelectors(useBearStoreBase)
 * 
 * // Usage in components:
 * const bears = useBearStore.use.bears()
 * const increment = useBearStore.use.increment()
 */
export function createSelectors<S extends UseBoundStore<StoreApi<object>>>(
  _store: S,
): WithSelectors<S> {
  const store = _store as WithSelectors<typeof _store>;
  store.use = {} as WithSelectors<typeof _store>['use'];
  
  for (const key of Object.keys(store.getState())) {
    (store.use as Record<string, () => unknown>)[key] = () =>
      store((state) => state[key as keyof typeof state]);
  }
  
  return store;
}

/**
 * Type-safe selector factory for complex derived state.
 * Use this when you need computed values that depend on multiple state properties.
 * 
 * @example
 * const selectUserDisplayName = createSelector(
 *   (state: BoundStoreState) => `${state.user?.firstName} ${state.user?.lastName}`
 * );
 * 
 * // In component:
 * const displayName = useBoundStore(selectUserDisplayName);
 */
export function createSelector<T, R>(
  selector: (state: T) => R,
): (state: T) => R {
  return selector;
}
